"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../contexts/AuthContext"
import { useWallet } from "../contexts/WalletContext"
import { Smartphone, Check } from "lucide-react"
import Card from "../components/Card"
import Input from "../components/Input"
import Button from "../components/Button"
import Modal from "../components/Modal"
import Loader from "../components/Loader"
import { getDataPlans, purchaseData } from "../utils/api"
import toast from "react-hot-toast"

const BuyData = () => {
  const { user } = useAuth()
  const { addTransaction, deductBalance } = useWallet()
  const navigate = useNavigate()

  const [selectedNetwork, setSelectedNetwork] = useState("")
  const [phoneNumber, setPhoneNumber] = useState("")
  const [selectedPlan, setSelectedPlan] = useState(null)
  const [dataPlans, setDataPlans] = useState([])
  const [loading, setLoading] = useState(false)
  const [purchasing, setPurchasing] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [errors, setErrors] = useState({})

  const networks = [
    { id: "mtn", name: "MTN", color: "bg-yellow-500", logo: "🟡" },
    { id: "airtel", name: "Airtel", color: "bg-red-500", logo: "🔴" },
    { id: "glo", name: "Glo", color: "bg-green-500", logo: "🟢" },
    { id: "9mobile", name: "9mobile", color: "bg-green-600", logo: "🟢" },
  ]

  useEffect(() => {
    if (!user) {
      navigate("/login")
    }
  }, [user, navigate])

  useEffect(() => {
    if (selectedNetwork) {
      loadDataPlans()
    }
  }, [selectedNetwork])

  const loadDataPlans = async () => {
    setLoading(true)
    try {
      const plans = await getDataPlans(selectedNetwork)
      setDataPlans(plans)
    } catch (error) {
      toast.error("Failed to load data plans")
    } finally {
      setLoading(false)
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!selectedNetwork) {
      newErrors.network = "Please select a network"
    }

    if (!phoneNumber) {
      newErrors.phone = "Phone number is required"
    } else if (!/^(\+234|0)[789][01]\d{8}$/.test(phoneNumber)) {
      newErrors.phone = "Please enter a valid Nigerian phone number"
    }

    if (!selectedPlan) {
      newErrors.plan = "Please select a data plan"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handlePurchase = () => {
    if (!validateForm()) return

    if (user.balance < selectedPlan.price) {
      toast.error("Insufficient wallet balance")
      return
    }

    setShowConfirmModal(true)
  }

  const confirmPurchase = async () => {
    setPurchasing(true)
    setShowConfirmModal(false)

    try {
      const purchaseResponse = await purchaseData({
        network: selectedNetwork,
        phone: phoneNumber,
        planId: selectedPlan.id,
        planName: selectedPlan.name,
        amount: selectedPlan.price,
      })

      if (purchaseResponse.code === "000") {
        // Deduct from wallet
        deductBalance(selectedPlan.price)

        // Add transaction
        addTransaction({
          type: "Data Purchase",
          network: selectedNetwork.toUpperCase(),
          phone: phoneNumber,
          amount: selectedPlan.price,
          plan: selectedPlan.name,
          status: "completed",
          transactionId: purchaseResponse.content.transactions.transactionId,
        })

        toast.success("Data purchase successful!")

        // Reset form
        setSelectedNetwork("")
        setPhoneNumber("")
        setSelectedPlan(null)
        setDataPlans([])

        // Redirect to transactions
        setTimeout(() => {
          navigate("/transactions")
        }, 2000)
      } else {
        toast.error("Purchase failed. Please try again.")
      }
    } catch (error) {
      toast.error("Purchase failed. Please try again.")
    } finally {
      setPurchasing(false)
    }
  }

  if (!user) return null

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <Smartphone className="mr-3 text-blue-600" />
            Buy Data
          </h1>
          <p className="text-gray-600 mt-2">Purchase data for any Nigerian network instantly</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form Section */}
          <div className="lg:col-span-2">
            <Card>
              <h2 className="text-xl font-semibold mb-6">Select Network & Plan</h2>

              {/* Network Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">Choose Network</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {networks.map((network) => (
                    <div
                      key={network.id}
                      onClick={() => {
                        setSelectedNetwork(network.id)
                        setSelectedPlan(null)
                        setErrors({})
                      }}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        selectedNetwork === network.id
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <div className="text-center">
                        <div
                          className={`w-12 h-12 ${network.color} rounded-full flex items-center justify-center mx-auto mb-2`}
                        >
                          <span className="text-xl">{network.logo}</span>
                        </div>
                        <p className="font-medium">{network.name}</p>
                      </div>
                    </div>
                  ))}
                </div>
                {errors.network && <p className="mt-1 text-sm text-red-600">{errors.network}</p>}
              </div>

              {/* Phone Number */}
              <Input
                label="Phone Number"
                type="tel"
                value={phoneNumber}
                onChange={(e) => {
                  setPhoneNumber(e.target.value)
                  if (errors.phone) {
                    setErrors({ ...errors, phone: "" })
                  }
                }}
                error={errors.phone}
                placeholder="e.g., 08123456789"
              />

              {/* Data Plans */}
              {selectedNetwork && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-3">Select Data Plan</label>
                  {loading ? (
                    <div className="py-8">
                      <Loader />
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {dataPlans.map((plan) => (
                        <div
                          key={plan.id}
                          onClick={() => {
                            setSelectedPlan(plan)
                            setErrors({})
                          }}
                          className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                            selectedPlan?.id === plan.id
                              ? "border-blue-500 bg-blue-50"
                              : "border-gray-200 hover:border-gray-300"
                          }`}
                        >
                          <div className="flex justify-between items-center">
                            <div>
                              <p className="font-semibold">{plan.data}</p>
                              <p className="text-sm text-gray-600">{plan.validity}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-lg">₦{plan.price}</p>
                              {selectedPlan?.id === plan.id && <Check className="h-5 w-5 text-blue-600 ml-auto" />}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {errors.plan && <p className="mt-1 text-sm text-red-600">{errors.plan}</p>}
                </div>
              )}

              <Button
                onClick={handlePurchase}
                disabled={!selectedNetwork || !phoneNumber || !selectedPlan || purchasing}
                loading={purchasing}
                className="w-full"
              >
                Purchase Data
              </Button>
            </Card>
          </div>

          {/* Summary Section */}
          <div>
            <Card>
              <h3 className="text-lg font-semibold mb-4">Purchase Summary</h3>

              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Network:</span>
                  <span className="font-medium">{selectedNetwork ? selectedNetwork.toUpperCase() : "-"}</span>
                </div>

                <div className="flex justify-between">
                  <span className="text-gray-600">Phone:</span>
                  <span className="font-medium">{phoneNumber || "-"}</span>
                </div>

                <div className="flex justify-between">
                  <span className="text-gray-600">Plan:</span>
                  <span className="font-medium">{selectedPlan ? selectedPlan.data : "-"}</span>
                </div>

                <div className="flex justify-between">
                  <span className="text-gray-600">Validity:</span>
                  <span className="font-medium">{selectedPlan ? selectedPlan.validity : "-"}</span>
                </div>

                <hr />

                <div className="flex justify-between text-lg font-bold">
                  <span>Total:</span>
                  <span className="text-red-600">₦{selectedPlan ? selectedPlan.price.toLocaleString() : "0"}</span>
                </div>

                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="flex justify-between text-sm">
                    <span>Wallet Balance:</span>
                    <span className={user.balance >= (selectedPlan?.price || 0) ? "text-green-600" : "text-red-600"}>
                      ₦{user.balance?.toLocaleString() || "0"}
                    </span>
                  </div>
                  {selectedPlan && user.balance < selectedPlan.price && (
                    <p className="text-red-600 text-xs mt-1">Insufficient balance. Please fund your wallet.</p>
                  )}
                </div>
              </div>
            </Card>
          </div>
        </div>

        {/* Confirmation Modal */}
        <Modal isOpen={showConfirmModal} onClose={() => setShowConfirmModal(false)} title="Confirm Purchase">
          <div className="space-y-4">
            <p>Are you sure you want to purchase the following data plan?</p>

            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              <div className="flex justify-between">
                <span>Network:</span>
                <span className="font-medium">{selectedNetwork?.toUpperCase()}</span>
              </div>
              <div className="flex justify-between">
                <span>Phone:</span>
                <span className="font-medium">{phoneNumber}</span>
              </div>
              <div className="flex justify-between">
                <span>Plan:</span>
                <span className="font-medium">{selectedPlan?.name}</span>
              </div>
              <div className="flex justify-between font-bold">
                <span>Amount:</span>
                <span className="text-red-600">₦{selectedPlan?.price.toLocaleString()}</span>
              </div>
            </div>

            <div className="flex space-x-4">
              <Button variant="outline" onClick={() => setShowConfirmModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={confirmPurchase} loading={purchasing} className="flex-1">
                Confirm Purchase
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  )
}

export default BuyData
